import { Script } from "@scure/btc-signer";
import type { BTC_NETWORK } from "@scure/btc-signer/utils.js";

import { createInscriptionPlan, DEFAULT_POSTAGE, type InscriptionPlan } from "./inscriptions";
import type { ConnectedWallet, MempoolUtxo } from "./types";

export const RUNE_ETCH_SERVICE_FEE_SATS = 2_000;
export const RUNE_COMMIT_CONFIRMATIONS = 6;
export const RUNE_REVEAL_SEQUENCE = RUNE_COMMIT_CONFIRMATIONS - 1;
export const RUNE_MAX_DIVISIBILITY = 38;
export const RUNE_MAX_VALUE = (1n << 128n) - 1n;
export const RUNE_RESERVED_VALUE = 6_402_364_363_415_443_603_228_541_259_936_211_926n;
export const RUNE_MAX_LOGO_BYTES = 400 * 1024;

const RUNE_UNLOCK_STEPS = [
  0n,
  26n,
  702n,
  18_278n,
  475_254n,
  12_356_630n,
  321_272_406n,
  8_353_082_582n,
  217_180_147_158n,
  5_646_683_826_134n,
  146_813_779_479_510n,
  3_817_158_266_467_286n,
  99_246_114_928_149_462n,
  2_580_398_988_131_886_038n,
  67_090_373_691_429_037_014n,
  1_744_349_715_977_154_962_390n,
  45_353_092_615_406_029_022_166n,
  1_179_180_408_000_556_754_576_342n,
  30_658_690_608_014_475_618_984_918n,
  797_125_955_808_376_366_093_607_894n,
  20_725_274_851_017_785_518_433_805_270n,
  538_857_146_126_462_423_479_278_937_046n,
  14_010_285_799_288_023_010_461_252_363_222n,
  364_267_430_781_488_598_271_992_561_443_798n,
  9_470_953_200_318_703_555_071_807_538_778_774n,
  246_244_783_208_286_292_431_866_971_536_008_150n,
  RUNE_RESERVED_VALUE,
  166_461_473_448_801_533_683_942_072_758_341_510_102n,
] as const;

const RUNE_FIRST_HEIGHT = 840_000;
const RUNE_UNLOCK_INTERVAL = 17_500;
const RUNE_FULLY_UNLOCKED_HEIGHT = 1_050_000;
const RUNE_INITIAL_UNLOCKED_LENGTH = 12;

export type RuneTermsMode = "height" | "offset";

export interface RuneEtchInput {
  spacedRune: string;
  symbol?: string;
  divisibility: number;
  premine?: string;
  mintable: boolean;
  amount?: string;
  cap?: string;
  termsMode?: RuneTermsMode;
  start?: string;
  end?: string;
  turbo?: boolean;
}

export interface RuneEtchTerms {
  amount: string;
  cap: string;
  height: [number | null, number | null];
  offset: [number | null, number | null];
}

export interface RuneEtchDefinition {
  spacedRune: string;
  rune: string;
  spacers: number;
  symbol: string;
  divisibility: number;
  premine: string;
  terms: RuneEtchTerms | null;
  turbo: boolean;
}

export type RuneEtchStatus = "prepared" | "maturing" | "reveal_broadcast" | "complete" | "failed";

export interface RuneEtchJobSummary {
  id: string;
  definition: RuneEtchDefinition;
  status: RuneEtchStatus;
  commitTxid: string;
  revealTxid: string;
  confirmations: number;
  createdAt: number;
  commitBroadcastAt: number | null;
  revealBroadcastAt: number | null;
  confirmedAt: number | null;
  lastError: string | null;
}

export interface RuneEtchAvailability {
  tipHeight: number;
  expectedRevealHeight: number;
  minimumRune: string;
  available: boolean | null;
  unlocked: boolean | null;
}

export type RuneLogoInput =
  | { kind: "none" }
  | { kind: "text"; text: string }
  | { kind: "file"; body: Uint8Array; mimeType: string; name: string };

export interface RuneEtchPlanOptions {
  wallet: ConnectedWallet;
  definition: RuneEtchDefinition;
  logo: RuneLogoInput;
  utxos: MempoolUtxo[];
  feeRate: number;
  marketplaceFeeAddress: string;
  network?: BTC_NETWORK;
}

function parseUnsignedInteger(value: string, label: string, allowZero = true): bigint {
  const clean = value.trim();
  if (!/^[0-9]+$/.test(clean)) throw new Error(`${label} must be a whole number`);
  const parsed = BigInt(clean);
  if ((!allowZero && parsed === 0n) || parsed > RUNE_MAX_VALUE) {
    throw new Error(`${label} is outside the Rune protocol range`);
  }
  return parsed;
}

export function runeAmountToAtomic(value: string, divisibility: number, label: string): bigint {
  const clean = value.trim();
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/.test(clean)) throw new Error(`${label} must be a positive decimal amount`);
  const [whole = "0", fraction = ""] = clean.split(".");
  if (fraction.length > divisibility) throw new Error(`${label} supports at most ${divisibility} decimal places`);
  const parsed = BigInt(`${whole}${fraction.padEnd(divisibility, "0")}`);
  if (parsed > RUNE_MAX_VALUE) throw new Error(`${label} is outside the Rune protocol range`);
  return parsed;
}

export function normalizeSpacedRune(value: string): { spacedRune: string; runeName: string; rune: bigint; spacers: number } {
  const spacedRune = value.trim().toLocaleUpperCase("en-US").replaceAll(".", "•");
  if (!spacedRune) throw new Error("Enter a Rune name");
  if (!/^[A-Z•]+$/.test(spacedRune)) throw new Error("Rune names use only A–Z and • spacers");
  if (spacedRune.startsWith("•") || spacedRune.endsWith("•") || spacedRune.includes("••")) {
    throw new Error("Spacers can appear only once between two letters");
  }
  const runeName = spacedRune.replaceAll("•", "");
  if (runeName.length < 1 || runeName.length > 26) throw new Error("Rune names contain between 1 and 26 letters");
  let rune = 0n;
  for (let index = 0; index < runeName.length; index += 1) {
    if (index > 0) rune += 1n;
    rune *= 26n;
    rune += BigInt((runeName.charCodeAt(index) || 65) - 65);
  }
  if (rune >= RUNE_RESERVED_VALUE) throw new Error("Reserved Rune names cannot be chosen manually");
  let spacers = 0;
  let letterIndex = 0;
  for (const character of spacedRune) {
    if (character === "•") spacers |= 1 << (letterIndex - 1);
    else letterIndex += 1;
  }
  return { spacedRune, runeName, rune, spacers };
}

export function runeNameFromValue(value: bigint): string {
  if (value < 0n || value > RUNE_MAX_VALUE) throw new Error("Rune value is outside the protocol range");
  if (value === RUNE_MAX_VALUE) return "BCGDENLQRQWDSLRUGSNLBTMFIJAV";
  let remainder = value + 1n;
  let name = "";
  while (remainder > 0n) {
    name = String.fromCharCode(65 + Number((remainder - 1n) % 26n)) + name;
    remainder = (remainder - 1n) / 26n;
  }
  return name;
}

export function minimumRuneAtHeight(height: number): bigint {
  if (!Number.isSafeInteger(height) || height < 0) throw new Error("Bitcoin height is invalid");
  const offset = height + 1;
  if (offset < RUNE_FIRST_HEIGHT) return RUNE_UNLOCK_STEPS[RUNE_INITIAL_UNLOCKED_LENGTH];
  if (offset >= RUNE_FULLY_UNLOCKED_HEIGHT) return 0n;
  const progress = offset - RUNE_FIRST_HEIGHT;
  const length = RUNE_INITIAL_UNLOCKED_LENGTH - Math.floor(progress / RUNE_UNLOCK_INTERVAL);
  const end = RUNE_UNLOCK_STEPS[length - 1] ?? 0n;
  const start = RUNE_UNLOCK_STEPS[length] ?? 0n;
  const remainder = BigInt(progress % RUNE_UNLOCK_INTERVAL);
  return start - ((start - end) * remainder / BigInt(RUNE_UNLOCK_INTERVAL));
}

function parseOptionalHeight(value: string | undefined, label: string): number | null {
  if (!value?.trim()) return null;
  if (!/^[0-9]+$/.test(value.trim())) throw new Error(`${label} must be a whole block number`);
  const height = Number(value.trim());
  if (!Number.isSafeInteger(height) || height < 0) throw new Error(`${label} is outside the supported block range`);
  return height;
}

export function normalizeRuneEtchDefinition(input: RuneEtchInput, expectedRevealHeight?: number): RuneEtchDefinition {
  const name = normalizeSpacedRune(input.spacedRune);
  if (!Number.isSafeInteger(input.divisibility) || input.divisibility < 0 || input.divisibility > RUNE_MAX_DIVISIBILITY) {
    throw new Error(`Divisibility must be between 0 and ${RUNE_MAX_DIVISIBILITY}`);
  }
  const symbols = Array.from(input.symbol?.trim() || "¤");
  if (symbols.length !== 1) throw new Error("Symbol must be one character");
  if (expectedRevealHeight !== undefined && name.rune < minimumRuneAtHeight(expectedRevealHeight)) {
    throw new Error(`${name.spacedRune} will still be locked at the expected reveal height`);
  }
  const premine = input.premine?.trim()
    ? runeAmountToAtomic(input.premine, input.divisibility, "Premine")
    : 0n;
  let terms: RuneEtchTerms | null = null;
  if (input.mintable) {
    const amount = runeAmountToAtomic(input.amount ?? "", input.divisibility, "Amount per mint");
    const cap = parseUnsignedInteger(input.cap ?? "", "Mint cap", false);
    if (amount === 0n) throw new Error("Amount per mint must be greater than zero");
    const start = parseOptionalHeight(input.start, "Mint start");
    const end = parseOptionalHeight(input.end, "Mint end");
    if (start !== null && end !== null && end <= start) throw new Error("Mint end must be greater than mint start");
    if (input.termsMode !== "offset" && expectedRevealHeight !== undefined) {
      if (start !== null && start <= expectedRevealHeight) throw new Error(`Absolute mint start must be after expected reveal block ${expectedRevealHeight}`);
      if (end !== null && end <= expectedRevealHeight) throw new Error(`Absolute mint end must be after expected reveal block ${expectedRevealHeight}`);
    }
    terms = {
      amount: amount.toString(),
      cap: cap.toString(),
      height: input.termsMode === "offset" ? [null, null] : [start, end],
      offset: input.termsMode === "offset" ? [start, end] : [null, null],
    };
  }
  const supply = premine + (terms ? BigInt(terms.amount) * BigInt(terms.cap) : 0n);
  if (supply < 1n) throw new Error("Total Rune supply must be greater than zero");
  if (supply > RUNE_MAX_VALUE) throw new Error("Premine plus mintable supply exceeds the Rune protocol maximum");
  return {
    spacedRune: name.spacedRune,
    rune: name.rune.toString(),
    spacers: name.spacers,
    symbol: symbols[0] ?? "¤",
    divisibility: input.divisibility,
    premine: premine.toString(),
    terms,
    turbo: input.turbo === true,
  };
}

function nullableHeightPair(value: unknown, label: string): [number | null, number | null] {
  if (!Array.isArray(value) || value.length !== 2) throw new Error(`${label} is invalid`);
  const pair = value.map((item) => {
    if (item === null) return null;
    if (!Number.isSafeInteger(item) || Number(item) < 0) throw new Error(`${label} is invalid`);
    return Number(item);
  });
  return [pair[0] ?? null, pair[1] ?? null];
}

export function validateRuneEtchDefinition(value: unknown, expectedRevealHeight?: number): RuneEtchDefinition {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("Rune definition is invalid");
  const item = value as Record<string, unknown>;
  if (typeof item.spacedRune !== "string" || typeof item.rune !== "string" || typeof item.spacers !== "number"
    || typeof item.symbol !== "string" || typeof item.divisibility !== "number" || typeof item.premine !== "string"
    || typeof item.turbo !== "boolean") throw new Error("Rune definition is incomplete");
  const premine = parseUnsignedInteger(item.premine, "Premine").toString();
  if (premine !== item.premine) throw new Error("Premine must use canonical atomic units");
  let terms: RuneEtchTerms | null = null;
  if (item.terms !== null) {
    if (typeof item.terms !== "object" || item.terms === null || Array.isArray(item.terms)) throw new Error("Rune mint terms are invalid");
    const rawTerms = item.terms as Record<string, unknown>;
    if (typeof rawTerms.amount !== "string" || typeof rawTerms.cap !== "string") throw new Error("Rune mint terms are incomplete");
    const amount = parseUnsignedInteger(rawTerms.amount, "Amount per mint", false).toString();
    const cap = parseUnsignedInteger(rawTerms.cap, "Mint cap", false).toString();
    if (amount !== rawTerms.amount || cap !== rawTerms.cap) throw new Error("Rune mint terms must use canonical atomic units");
    terms = {
      amount,
      cap,
      height: nullableHeightPair(rawTerms.height, "Absolute mint range"),
      offset: nullableHeightPair(rawTerms.offset, "Relative mint range"),
    };
    if (terms.height.some((entry) => entry !== null) && terms.offset.some((entry) => entry !== null)) {
      throw new Error("Choose either absolute or relative mint heights");
    }
  }
  const candidate: RuneEtchDefinition = {
    spacedRune: item.spacedRune,
    rune: item.rune,
    spacers: item.spacers,
    symbol: item.symbol,
    divisibility: item.divisibility,
    premine,
    terms,
    turbo: item.turbo,
  };
  const normalized = normalizeRuneEtchDefinition({
    spacedRune: candidate.spacedRune,
    symbol: candidate.symbol,
    divisibility: candidate.divisibility,
    premine: atomicRuneAmountForInput(candidate.premine, candidate.divisibility),
    mintable: candidate.terms !== null,
    amount: candidate.terms ? atomicRuneAmountForInput(candidate.terms.amount, candidate.divisibility) : undefined,
    cap: candidate.terms?.cap,
    termsMode: candidate.terms?.offset.some((entry) => entry !== null) ? "offset" : "height",
    start: String(candidate.terms?.height[0] ?? candidate.terms?.offset[0] ?? ""),
    end: String(candidate.terms?.height[1] ?? candidate.terms?.offset[1] ?? ""),
    turbo: candidate.turbo,
  }, expectedRevealHeight);
  if (JSON.stringify(normalized) !== JSON.stringify(candidate)) throw new Error("Rune definition is not canonical");
  return normalized;
}

function pushVarint(target: number[], value: bigint): void {
  let remaining = value;
  do {
    let byte = Number(remaining & 0x7fn);
    remaining >>= 7n;
    if (remaining > 0n) byte |= 0x80;
    target.push(byte);
  } while (remaining > 0n);
}

function pushField(target: number[], tag: bigint, value: bigint | null): void {
  if (value === null) return;
  pushVarint(target, tag);
  pushVarint(target, value);
}

export function runeEtchRunestoneScript(definition: RuneEtchDefinition, premineOutputIndex = 2): Uint8Array {
  const payload: number[] = [];
  const flags = 1n | (definition.terms ? 2n : 0n) | (definition.turbo ? 4n : 0n);
  pushField(payload, 2n, flags);
  pushField(payload, 4n, BigInt(definition.rune));
  pushField(payload, 1n, definition.divisibility > 0 ? BigInt(definition.divisibility) : null);
  pushField(payload, 3n, definition.spacers > 0 ? BigInt(definition.spacers) : null);
  pushField(payload, 5n, BigInt(definition.symbol.codePointAt(0) ?? 0x00a4));
  pushField(payload, 6n, BigInt(definition.premine) > 0n ? BigInt(definition.premine) : null);
  if (definition.terms) {
    pushField(payload, 10n, BigInt(definition.terms.amount));
    pushField(payload, 8n, BigInt(definition.terms.cap));
    pushField(payload, 12n, definition.terms.height[0] === null ? null : BigInt(definition.terms.height[0]));
    pushField(payload, 14n, definition.terms.height[1] === null ? null : BigInt(definition.terms.height[1]));
    pushField(payload, 16n, definition.terms.offset[0] === null ? null : BigInt(definition.terms.offset[0]));
    pushField(payload, 18n, definition.terms.offset[1] === null ? null : BigInt(definition.terms.offset[1]));
  }
  if (BigInt(definition.premine) > 0n) pushField(payload, 22n, BigInt(premineOutputIndex));
  return Script.encode(["RETURN", 13, Uint8Array.from(payload)]);
}

export function runeCommitment(rune: bigint): Uint8Array {
  if (rune < 0n || rune > RUNE_MAX_VALUE) throw new Error("Rune value is outside the protocol range");
  const bytes: number[] = [];
  let remaining = rune;
  while (remaining > 0n) {
    bytes.push(Number(remaining & 0xffn));
    remaining >>= 8n;
  }
  return Uint8Array.from(bytes);
}

function logoDraft(logo: RuneLogoInput): { body: Uint8Array; mimeType?: string; name: string } {
  if (logo.kind === "none") return { body: new Uint8Array(), name: "Rune commitment" };
  if (logo.kind === "text") {
    const body = new TextEncoder().encode(logo.text);
    if (!body.length) throw new Error("Enter logo text or choose no logo");
    if (body.length > RUNE_MAX_LOGO_BYTES) throw new Error("Rune logo text exceeds the 400 KB inscription limit");
    return { body, mimeType: "text/plain;charset=utf-8", name: "Rune logo text" };
  }
  if (!logo.body.length) throw new Error("Choose a non-empty Rune logo file");
  if (logo.body.length > RUNE_MAX_LOGO_BYTES) throw new Error("Rune logo file exceeds the 400 KB inscription limit");
  if (!logo.mimeType.trim()) throw new Error("Rune logo file type is missing");
  return { body: logo.body, mimeType: logo.mimeType, name: logo.name || "Rune logo" };
}

export function createRuneEtchPlan(options: RuneEtchPlanOptions): InscriptionPlan {
  const definition = validateRuneEtchDefinition(options.definition);
  const logo = logoDraft(options.logo);
  const hasPremine = BigInt(definition.premine) > 0n;
  return createInscriptionPlan({
    wallet: options.wallet,
    drafts: [{
      id: crypto.randomUUID(),
      name: logo.name,
      body: logo.body,
      mimeType: logo.mimeType,
      postage: DEFAULT_POSTAGE,
      rune: BigInt(definition.rune),
    }],
    utxos: options.utxos,
    commitFeeRate: options.feeRate,
    revealFeeRate: options.feeRate,
    marketplaceFeeAddress: options.marketplaceFeeAddress,
    marketplaceFeeSats: RUNE_ETCH_SERVICE_FEE_SATS,
    allowTaprootPayment: true,
    commitInputSequence: 0xffffffff,
    revealOptions: {
      ephemeralReveal: true,
      transactionVersion: 2,
      inputSequence: RUNE_REVEAL_SEQUENCE,
      opReturnScript: runeEtchRunestoneScript(definition),
      additionalOutputs: hasPremine
        ? [{ address: options.wallet.ordinals.address, amountSats: DEFAULT_POSTAGE }]
        : [],
    },
    network: options.network,
  });
}

export function atomicRuneAmountForInput(value: string, divisibility: number): string {
  const atomic = parseUnsignedInteger(value, "Rune amount");
  if (divisibility === 0) return atomic.toString();
  const padded = atomic.toString().padStart(divisibility + 1, "0");
  const whole = padded.slice(0, -divisibility);
  const fraction = padded.slice(-divisibility).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

export function runeSupply(definition: RuneEtchDefinition): bigint {
  return BigInt(definition.premine)
    + (definition.terms ? BigInt(definition.terms.amount) * BigInt(definition.terms.cap) : 0n);
}

export function formatRuneAmount(value: bigint, divisibility: number): string {
  return atomicRuneAmountForInput(value.toString(), divisibility);
}
